import { useCallback, useEffect, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import { clampCustomScaleToSheet, clampScaleToOffered } from '../../lib/mapPrintLimits'
import type { Size } from '../../lib/printLayout'
import {
  MAX_PAGE_ASPECT,
  MIN_PAGE_ASPECT,
  pageAspect,
  pageForAspect,
  type PageMm,
} from '../../lib/printCustomSheet'
import type { PaperFormat, PrintOrientation } from '../../lib/printPaper'
import { pageMm } from '../../lib/printPaper'
import { pageExceedsViewport, pageForViewport, scaleForViewport } from '../../lib/printFromViewport'
import type { PrintProjector } from '../../lib/printProjection'
import { PRINT_SCALES } from '../../lib/printScale'
import type { GeoPt } from '../../lib/types'
import type { MapUnprojectFn } from './MapProjectionProbe'

/** Which custom control the camera drives, or null while neither is chosen. */
export type ActiveCustomControl = 'page' | 'scale'

/** What one pass of the camera derivation produces. */
export interface DerivedSheet {
  page?: PageMm
  scale?: number
  /** Whether the page range stopped that page from fitting the view. */
  exceedsView?: boolean
}

/**
 * The scale a custom sheet starts at, where the user has not chosen one.
 *
 * 1:50 000 rather than the panel's everyday default because it is the most
 * useful scale for a hunting ground, and the measurements say it is affordable
 * even at the extreme: the largest sheet this UI can express renders in 483 s
 * against a 4-hour server timeout. It is the peak of the render-cost curve, and
 * it is worth encouraging deliberately rather than merely permitting.
 */
export const DEFAULT_CUSTOM_SCALE = 50000

export interface CustomPrintSheet {
  format: PaperFormat
  orientation: PrintOrientation
  /** The scale the print will actually use, clamped to what the sheet allows. */
  scale: number
  /** The custom sheet's millimetres while one is chosen, else undefined. */
  page: PageMm | undefined
  /**
   * The width-over-height ratio the slider is set to.
   *
   * Held separately from the page's own `pageAspect` on purpose. The page is
   * rounded to whole millimetres, so its measured ratio is never exactly the
   * one that was asked for — and the camera derivation reads this to decide
   * the sheet's SHAPE while deciding its SIZE from the viewport. Reading the
   * rounded page instead would feed each derivation its own rounding error and
   * let the sheet drift a millimetre at a time for as long as the panel stayed
   * open.
   */
  aspect: number
  /** The chosen custom scale, or undefined while a fixed one is selected. */
  customScale: number | undefined
  customPageActive: boolean
  customScaleActive: boolean
  /**
   * True while a custom control is being driven by the camera — hand to
   * `usePrintFrame`, which must then not fit the map back to a sheet the camera
   * itself produced.
   */
  followsCamera: boolean
  /** Names the sheet in the job list: "A3 landscape", or "640 × 480 mm". */
  pageLabel: string
  onFormatChange: (format: PaperFormat) => void
  onOrientationChange: (orientation: PrintOrientation) => void
  onScaleChange: (scale: number) => void
  onCustomPageChange: (page: PageMm) => void
  /**
   * The slider. It always sets the ratio; it reshapes the page directly as well,
   * which is what moves the frame while the SCALE is the control following the
   * camera — there being nothing else to reshape it then.
   */
  onAspectChange: (aspect: number) => void
  onCustomScaleChange: (scale: number) => void
  onActivateCustomPage: () => void
  onActivateCustomScale: () => void
  /** Which control the camera drives, null while neither is custom. */
  activeCustom: ActiveCustomControl | null
  /**
   * Bumped when the user's own change to the sheet has produced one the map
   * cannot hold — hand to `usePrintFrame`'s `refitToken`, which then zooms out
   * to it exactly once.
   *
   * Both halves are load-bearing. It is minted by the DERIVATION, so by the
   * time it moves the sheet it refers to already exists and the fit cannot land
   * on an intermediate shape. And it is claimed by a GESTURE — arriving at
   * Custom, dragging the ratio, handing the camera back to the page — never by
   * the overflow alone, because at the 100 mm floor the overflow is permanent:
   * the sheet has stopped shrinking with the view, so a rule that fired on the
   * condition would pull the camera out of every attempt to zoom in, and again
   * on a window resize. The sheet follows the map when the map is moved; the
   * map follows the sheet when the sheet is changed. Never both from one
   * gesture.
   *
   * Whether the sheet fits is decided where the PAGE was decided — see
   * `pageExceedsViewport` — and not by re-measuring the frame's corners against
   * the map, which is a different plane and does not give the same answer.
   */
  zoomOutToken: number
  /** The seam `useSheetFromCamera` writes through; not for hosts to call. */
  applyDerived: (derived: DerivedSheet) => void
}

/**
 * The print panel's sheet choice — paper, orientation, scale, and the two
 * custom controls that can replace either of them.
 *
 * Shared rather than written twice for the same reason `usePrintFrame` is:
 * `TeamMapPrintView` and the map editor's print mode make exactly the same
 * choice, and a rule kept in two copies is a rule that will eventually disagree
 * with itself. The STATE is per-surface — each host calls this — but the rules
 * relating the five pieces live here only.
 *
 * The rule that needs stating: the page and the scale can BOTH be custom, and
 * then exactly one of them follows the camera while the other holds. Zoom
 * cannot drive both, because a fixed page at a fixed scale already determines
 * the ground exactly — there would be nothing left for zoom to change.
 */
export function useCustomPrintSheet(): CustomPrintSheet {
  const [format, setFormat] = useState<PaperFormat>('a4')
  // Never overwritten while a custom sheet is selected: a custom sheet states
  // both its edges, so it has no orientation to swap. That is what makes the
  // round trip back to a named format lossless, with no separate "the
  // orientation they last had" to keep in step with this one.
  const [orientation, setOrientation] = useState<PrintOrientation>('portrait')
  const [requestedScale, setRequestedScale] = useState<number>(PRINT_SCALES[1])
  // Whether the scale showing is one the user picked. A custom sheet opened
  // without one starts at DEFAULT_CUSTOM_SCALE, and this is how that default
  // avoids overriding a deliberate choice made earlier in the same session.
  const [scaleChosen, setScaleChosen] = useState(false)
  const [customPage, setCustomPage] = useState<PageMm | null>(null)
  const [aspect, setAspect] = useState(1)
  const [customScale, setCustomScale] = useState<number | null>(null)
  // A change the user made to the sheet, waiting to find out whether the sheet
  // it produced fits the map. A ref rather than state because nothing renders
  // differently for it: it is a debt the next derivation settles either way.
  const refitOwedRef = useRef(false)
  const askForRefit = useCallback(() => {
    refitOwedRef.current = true
  }, [])
  const [zoomOutToken, setZoomOutToken] = useState(0)
  // Consulted only when BOTH controls are custom; with one custom control it is
  // always the active one, whatever this last said.
  const [preferredActive, setPreferredActive] = useState<ActiveCustomControl>('page')

  const page = format === 'custom' && customPage !== null ? customPage : undefined

  const activeCustom: ActiveCustomControl | null =
    page !== undefined && customScale !== null
      ? preferredActive
      : page !== undefined
        ? 'page'
        : customScale !== null
          ? 'scale'
          : null

  // A custom scale is taken as given: the series it comes from is inside the
  // server's range by construction, and where the PAGE makes it illegal the
  // panel names the breach and withholds the print — more honest than quietly
  // printing a different scale than the button shows. A fixed scale IS clamped,
  // because the paper row can withdraw one that was legal when it was picked.
  const scale =
    customScale ?? clampScaleToOffered(requestedScale, format, orientation, page)

  const pageLabel =
    page !== undefined ? `${page.w} × ${page.h} mm` : `${format.toUpperCase()} ${orientation}`

  const onFormatChange = useCallback(
    (next: PaperFormat) => {
      setFormat(next)
      if (next === 'custom') {
        if (!scaleChosen) setRequestedScale(DEFAULT_CUSTOM_SCALE)
        return
      }
      // Dropped on the way out rather than kept for a later return to Custom:
      // the panel re-seeds the millimetres from whichever sheet is showing when
      // Custom is next pressed, so a kept value could only ever be a staler
      // answer to the same question.
      setCustomPage(null)
    },
    [scaleChosen],
  )

  const onScaleChange = useCallback((next: number) => {
    setRequestedScale(next)
    setScaleChosen(true)
    // Picking a fixed scale is how the custom one is deselected — the panel
    // reads "no custom scale" as "a fixed scale is selected".
    setCustomScale(null)
  }, [])

  const onCustomScaleChange = useCallback((next: number) => {
    setCustomScale(next)
    setScaleChosen(true)
    askForRefit()
  }, [askForRefit])

  // Arriving at Custom brings the showing sheet's ratio with it, so the slider
  // starts where the paper the user was looking at sits rather than at 1.
  const onCustomPageChange = useCallback((next: PageMm) => {
    setCustomPage(next)
    setAspect(pageAspect(next))
    askForRefit()
  }, [askForRefit])

  const onAspectChange = useCallback((next: number) => {
    const wanted = Math.min(MAX_PAGE_ASPECT, Math.max(MIN_PAGE_ASPECT, next))
    setAspect(wanted)
    // Reshaped here unconditionally, even though a page the camera is sizing is
    // re-derived at the new ratio a moment later and this intermediate shape is
    // discarded. Skipping the write when the page looks camera-driven is wrong:
    // whether the camera can ACTUALLY size it depends on a projector that may
    // be absent (a region with no national grid) or still loading, and a page
    // whose only writer declined leaves the slider dead. One extra render per
    // slider step is the cheaper mistake.
    setCustomPage((prev) => (prev ? pageForAspect(prev, wanted) : prev))
    askForRefit()
  }, [askForRefit])

  const onActivateCustomPage = useCallback(() => {
    setPreferredActive('page')
    askForRefit()
  }, [askForRefit])
  const onActivateCustomScale = useCallback(() => {
    setPreferredActive('scale')
    askForRefit()
  }, [askForRefit])

  // Identity-preserving on an unchanged value, so a derivation that lands on
  // the same answer as last time ends the render cascade instead of continuing
  // it — which is what keeps the camera and the sheet from trading renders.
  const applyDerived = useCallback((derived: DerivedSheet) => {
    const nextPage = derived.page
    if (nextPage) {
      setCustomPage((prev) => (prev && prev.w === nextPage.w && prev.h === nextPage.h ? prev : nextPage))
    }
    if (derived.exceedsView !== undefined) {
      // Settled either way — a gesture whose sheet fits the map is owed
      // nothing, and leaving the debt standing would spend it on a later
      // derivation the user did not ask for.
      if (refitOwedRef.current) {
        refitOwedRef.current = false
        if (derived.exceedsView) setZoomOutToken((n) => n + 1)
      }
    }
    const nextScale = derived.scale
    if (nextScale !== undefined) {
      setCustomScale((prev) => (prev === nextScale ? prev : nextScale))
    }
  }, [])

  return {
    format,
    orientation,
    scale,
    page,
    aspect,
    customScale: customScale ?? undefined,
    customPageActive: activeCustom === 'page',
    customScaleActive: activeCustom === 'scale',
    followsCamera: activeCustom !== null,
    pageLabel,
    onFormatChange,
    onOrientationChange: setOrientation,
    onScaleChange,
    onCustomPageChange,
    onAspectChange,
    onCustomScaleChange,
    onActivateCustomPage,
    onActivateCustomScale,
    activeCustom,
    zoomOutToken,
    applyDerived,
  }
}

export interface UseSheetFromCameraArgs {
  padding?: { top: number; right: number; bottom: number; left: number }
  sheet: CustomPrintSheet
  /** False while the print surface is not showing — the editor's closed panel. */
  enabled: boolean
  center: GeoPt | null
  zoom: number | null
  containerPx: Size | null
  /** The sheet's own projector, from `usePrintFrame`. */
  projector: PrintProjector | null
  /** The live map's screen → lon/lat transform, from `MapProjectionProbe`. */
  unprojectRef: MutableRefObject<MapUnprojectFn | null>
}

/**
 * Keeps the active custom control in step with what the map is showing.
 *
 * Measured through the sheet's own projector rather than off the screen's
 * lon/lat span: the preview is Web Mercator and the sheet is a national grid,
 * so the same screen rectangle is a different ground rectangle at different
 * latitudes (see `viewportGroundExtentM`).
 *
 * It runs on activation as well as on every camera move — making a control
 * active is the user saying "let this follow the map", and waiting for the next
 * pan to honour that would leave the button stating a size the map does not
 * show.
 *
 * **Each branch is deaf to its own output, deliberately.** The page derivation
 * depends on the REQUESTED ratio and on the scale, never on the page it just
 * produced; the scale derivation on the page but not on the scale. Rounding to
 * whole millimetres moves a page's measured ratio a hair, so a page derivation
 * that re-read its own output's ratio would chase that hair back and forth for
 * as long as the panel stayed open — which is why `CustomPrintSheet.aspect` is
 * held as its own state rather than measured off the page.
 *
 * The page branch's dependency on the scale is a real cycle — a derived page
 * can be large enough that `clampScaleToOffered` withdraws the scale that
 * produced it — and it terminates for two reasons. A coarser scale always asks
 * for a SMALLER page, so the clamp cannot chase itself upward; and
 * `applyDerived` preserves identity on an unchanged value, so the second pass
 * over the same viewport ends the render cascade instead of continuing it.
 */
export function useSheetFromCamera({
  sheet, enabled, center, zoom, containerPx, projector, unprojectRef, padding,
}: UseSheetFromCameraArgs): void {
  const { activeCustom, page, aspect, format, orientation, scale, applyDerived } = sheet
  // Read by millimetres, not by object identity: a host that rebuilds the page
  // each render would otherwise re-run the derivation on every render.
  const pageW = page?.w
  const pageH = page?.h

  // Spread into primitives, never held as an object: a fresh `{ center, zoom }`
  // per render would re-run both derivations on every render, which is the
  // chase this whole hook is shaped to avoid.
  const live = enabled && center !== null && zoom !== null && containerPx !== null && projector !== null
  const lat = center?.lat
  const lon = center?.lon
  const left = padding?.left ?? 0, top = padding?.top ?? 0
  const pxW = containerPx ? Math.max(1, containerPx.w - left - (padding?.right ?? 0)) : undefined
  const pxH = containerPx ? Math.max(1, containerPx.h - top - (padding?.bottom ?? 0)) : undefined

  /** The viewport arguments, or null while any part of them is missing. */
  const readCamera = () => {
    const unproject = unprojectRef.current
    if (!live || !projector || !unproject || pxW === undefined || pxH === undefined) return null
    return { projector, unproject: (p: [number, number]) => unproject([p[0] + left, p[1] + top]), containerPx: { w: pxW, h: pxH } }
  }

  // ── The page follows the camera ──
  const pageActive = activeCustom === 'page'
  useEffect(() => {
    const view = pageActive ? readCamera() : null
    if (!view) return
    const derived = pageForViewport({
      ...view,
      scale,
      // The slider owns the ratio and the camera only decides how large a
      // sheet at that ratio may be, so dragging the slider is answered here
      // rather than by moving the map. Where the page range will not let the
      // sheet fit the view, the map is zoomed out to the sheet instead — which
      // is what the second half of this reports.
      aspect,
    })
    applyDerived({
      page: derived,
      exceedsView: pageExceedsViewport({ ...view, page: derived, scale }),
    })
  }, [pageActive, live, projector, lat, lon, zoom, pxW, pxH, left, top, scale, aspect, applyDerived])

  // ── The scale follows the camera ──
  const scaleActive = activeCustom === 'scale'
  // The sheet the scale is measured against: the custom page when there is one,
  // otherwise the named format's own table entry.
  const measured =
    pageW !== undefined && pageH !== undefined ? { w: pageW, h: pageH } : pageMm(format, orientation)
  const measuredKey = `${measured.w}x${measured.h}`
  useEffect(() => {
    const view = scaleActive ? readCamera() : null
    if (!view) return
    // Capped to what this sheet can legally render: the scale is being written
    // by the panel rather than by the user, so an uncapped one would disable
    // Create PDF from a gesture made on the map.
    const derived = clampCustomScaleToSheet(
      scaleForViewport({ ...view, page: measured }),
      { page: measured },
    )
    applyDerived({
      scale: derived,
      // Symmetric with the page branch: the scale series has a floor of its
      // own, so a very large sheet can be bigger than the view at every scale
      // offered, and then the map has to come to it.
      exceedsView: pageExceedsViewport({ ...view, page: measured, scale: derived }),
    })
    // `measured` is depended on through `measuredKey`, its stable half.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleActive, live, projector, lat, lon, zoom, pxW, pxH, left, top, measuredKey, applyDerived])
}
